import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  Button,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";


export default function Decision(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  //const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [mode, setMode] = React.useState<"stay" | "leave" | null>(null);
  const [selected, setSelected] = React.useState<string[]>([]);

  /* ---------------- LOAD DECISION ---------------- */

  React.useEffect(() => {
    loadDecision();
  }, []);

  async function loadDecision() {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) return;

    const { data } = await supabase
      .from("workshop_participant_decisions")
      .select("stay_or_leave")
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id)
      .single();

    if (data?.stay_or_leave) {
      setMode(data.stay_or_leave);
    }
  }

  /* ---------------- HANDLERS ---------------- */

  function toggleSelection(type: "individual" | "societal") {
    setSelected((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  }

  async function handleContinue() {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) return;

    await supabase
      .from("workshop_participant_decisions")
      .update({
        resilience_type: selected, // 🔥 ARRAY now
      })
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id);

    handlePageChange("PrepareYourself");
  }

  /* ---------------- UI ---------------- */

  if (!mode) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/leaveOrstay.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: "min(900px, 90vw)",
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        {mode === "stay" ? (
          <Stack spacing={5} alignItems="center">

            {/* TITLE */}
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "1.5rem", sm: "2rem" },
                fontWeight: 800,
              }}
            >
              {isDE
                ? "Wenn du bleibst - wofür entscheidest du dich?"
                : "If you stay - what do you choose?"}
            </Typography>

            {/* SUBTEXT */}
            <Typography sx={{ color: "rgba(255,255,255,0.75)", maxWidth: 700 }}>
              {isDE
                ? "Die Entscheidung hängt von mehreren Kriterien ab: persönliche und familiäre Bindungen, Sicherheitsüberlegungen, wirtschaftliche und berufliche Aspekte sowie emotionale und psychologische Faktoren."
                : "This decision depends on multiple factors: personal and family ties, security considerations, economic and professional aspects, as well as emotional and psychological factors."}
            </Typography>

            <Typography sx={{ color: "rgba(255,255,255,0.75)", maxWidth: 700 }}>
              {isDE
                ? "Du kannst eine oder beide Optionen auswählen."
                : "You can select one or both options."}
            </Typography>

            {/* OPTIONS */}
            <Grid container spacing={3}>

              <Grid item xs={12} md={6}>
                <Paper
                  onClick={() => toggleSelection("individual")}
                  sx={{
                    ...cardStyleRed,
                    border: selected.includes("individual")
                      ? "2px solid #fff"
                      : "1px solid rgba(255,255,255,0.15)",
                    transform: selected.includes("individual") ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  <Stack spacing={2}>
                    <Typography sx={{ color: "#fff", fontWeight: 800 }}>
                      {isDE ? "INDIVIDUELLE RESILIENZ" : "INDIVIDUAL RESILIENCE"}
                    </Typography>
                    <Typography sx={{ color: "#ff8a8a", fontWeight: 700 }}>
                      {isDE ? "Selbstschutz - BEREIT" : "Self-protection - PREPARE"}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                      {isDE
                        ? "Ich möchte mich vorbereiten, um meinen Beitrag zu leisten."
                        : "I want to prepare to contribute to resilience."}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  onClick={() => toggleSelection("societal")}
                  sx={{
                    ...cardStyleBlue,
                    border: selected.includes("societal")
                      ? "2px solid #fff"
                      : "1px solid rgba(255,255,255,0.15)",
                    transform: selected.includes("societal") ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  <Stack spacing={2}>
                    <Typography sx={{ color: "#fff", fontWeight: 800 }}>
                      {isDE ? "GESELLSCHAFTLICHE RESILIENZ" : "SOCIETAL RESILIENCE"}
                    </Typography>
                    <Typography sx={{ color: "#baacff", fontWeight: 700 }}>
                      {isDE ? "Engagement - GEMEINSAM" : "Engagement - SHARE"}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.85)" }}>
                      {isDE
                        ? "Ich möchte mich engagieren und anderen helfen."
                        : "I want to actively engage and help others."}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

            </Grid>
            <Button
              variant="contained"
              disabled={selected.length === 0}
              onClick={handleContinue}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 3,
                color: "white",
                fontSize: "1rem",
                fontWeight: 700,
                background: "linear-gradient(90deg, #710101, #ff0000)",

                "&:hover": {
                  background: "linear-gradient(90deg, #480000, #940000)",
                },

                "&.Mui-disabled": {
                  background: "#555",
                  color: "#aaa",
                  opacity: 1,
                },
              }}
            >
              {isDE ? "Weiter" : "Continue"}
            </Button>
          </Stack>
        ) : (
          /* 🔥 LEAVE MODE */
          <Stack spacing={5} alignItems="center">

            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "1.6rem", sm: "2rem" },
                fontWeight: 800,
              }}
            >
              {isDE
                ? "Wenn du gehst..."
                : "If you leave..."}
            </Typography>

            <Typography sx={{ color: "rgba(255,255,255,0.8)", maxWidth: 650 }}>
              {isDE
                ? "Wenn du dich entscheidest zu gehen, stellen sich drei entscheidende Fragen:"
                : "If you decide to leave, three critical questions arise:"}
            </Typography>

            <Stack spacing={2}>
              <Typography sx={{ color: "#ff5a5a", fontWeight: 700 }}>
                {isDE ? "Was ist mit deinen Rollen?" : "What about your roles?"}
              </Typography>

              <Typography sx={{ color: "#ff5a5a", fontWeight: 700 }}>
                {isDE ? "Wann gehst du?" : "When do you leave?"}
              </Typography>

              <Typography sx={{ color: "#ff5a5a", fontWeight: 700 }}>
                {isDE ? "Wohin gehst du?" : "Where do you go?"}
              </Typography>
            </Stack>

            <Typography sx={{ color: "rgba(255,255,255,0.6)", maxWidth: 600 }}>
              {isDE
                ? "Diese Fragen entscheiden über Geschwindigkeit, Sicherheit und Erfolg deiner Entscheidung."
                : "These questions determine the speed, safety, and success of your decision."}
            </Typography>

            <Button
              variant="outlined"
              onClick={() => handlePageChange("JourneyPillars")}
              sx={{
                mt: 2,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.4)",
                fontWeight: 700,
                px: 3,
                py: 1.2,
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              {isDE ? "Zurück zur Übersicht" : "Take me HOME"}
            </Button>

          </Stack>
        )}
      </Paper>
    </Box>
  );
}

/* ---------------- STYLES ---------------- */

const cardStyleRed = {
  p: 4,
  borderRadius: 4,
  cursor: "pointer",
  background: "linear-gradient(135deg, #ff2d2d, #310101)",
  border: "1px solid rgba(255,255,255,0.15)",
  transition: "all .25s ease",
  "&:hover": {
    transform: "scale(1.04)",
    boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
  },
};

const cardStyleBlue = {
  p: 4,
  borderRadius: 4,
  cursor: "pointer",
  background: "linear-gradient(135deg, #7b61ff, #0c0141)",
  border: "1px solid #f4f2fd21",
  transition: "all .25s ease",
  "&:hover": {
    transform: "scale(1.04)",
    boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
  },
};